import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { finalize } from 'rxjs/operators';
import { AppState } from '@app/core/state/appState';
import { Client } from 'src/app/core/entity/client';
import { LundifyConnection } from 'src/app/core/entity/lundify-connection';
import { LundifyShareRecipientRole, LundifySharedDocument } from 'src/app/core/entity/lundify-shared-document';
import { CloudCompanyService } from 'src/app/core/services/cloud-company.service';
import { DocumentService } from 'src/app/core/services/document.service';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';

interface GrantedAccess {
  cloudUserId: number;
  role: LundifyShareRecipientRole;
}

// The "Share <filename>" / "Manage access" overlay. Two entry points:
// - a freshly picked file (`file`): it is uploaded once, either with the first invited recipient
//   or without recipients ("Ladda upp utan att dela"). The response carries the documentShareId.
// - an already uploaded document (`sharedDocument`): its current recipients are preloaded.
// Once a documentShareId is known, recipients are added, changed and removed through the
// provider recipients endpoints (POST/PATCH/DELETE /sp/document/shared/{id}/recipients), so the
// file is never re-sent.
@Component({
  selector: 'app-lundify-share-dialog',
  templateUrl: './lundify-share-dialog.component.html',
})
export class LundifyShareDialogComponent {
  @Input() client: Client | undefined;

  @Input()
  get visible(): boolean {
    return this._visible;
  }
  set visible(value: boolean) {
    const wasVisible = this._visible;
    this._visible = value;

    if (value && !wasVisible) {
      this.resetState();
      this.loadLundifyUsers();
    }
  }

  // The file picked by the user, not yet uploaded.
  @Input() file: File | null = null;

  // An already uploaded document whose access is managed; mutually exclusive with `file`.
  @Input() sharedDocument: LundifySharedDocument | null = null;

  // Destination folder (slash-separated, no file name) sent as the share's "path"; empty = root.
  @Input() path = '';

  @Output() visibleChange = new EventEmitter<boolean>();

  // Emitted when something changed on the BLA side, so the caller can refresh the list.
  @Output() shared = new EventEmitter<void>();

  readonly roleOptions: { label: string; value: LundifyShareRecipientRole }[] = [
    { label: 'Kan läsa', value: 'READ' },
    { label: 'Kan godkänna', value: 'APPROVE' },
  ];

  lundifyUsers: LundifyConnection[] = [];
  loadingUsers = false;
  inviting = false;

  selectedUserId: number | null = null;
  selectedRole: LundifyShareRecipientRole = 'READ';
  access: GrantedAccess[] = [];
  pendingRoleChanges = new Set<number>();

  // Known once the document exists in BLA (existing document or after the first upload).
  private documentShareId: number | null = null;
  private _visible = false;

  constructor(
    private cloudCompanyService: CloudCompanyService,
    private documentService: DocumentService,
    private store: Store<AppState>,
  ) {}

  get fileName(): string {
    return this.file?.name ?? this.sharedDocument?.fileName ?? '';
  }

  get uploaded(): boolean {
    return this.documentShareId !== null;
  }

  get existingDocument(): boolean {
    return !!this.sharedDocument;
  }

  get canInvite(): boolean {
    return !!this.selectedUserId && (!!this.file || this.uploaded) && !this.inviting;
  }

  get canUploadWithoutRecipients(): boolean {
    return !!this.file && !this.uploaded && !this.inviting;
  }

  // Already invited users are dropped from the picker so the same person can't be added twice.
  get invitableUsers(): LundifyConnection[] {
    return this.lundifyUsers.filter((user) => !this.access.some((granted) => granted.cloudUserId === user.id));
  }

  userLabel(user: LundifyConnection): string {
    return user.email ? `${user.name} (${user.email})` : user.name;
  }

  userName(cloudUserId: number): string {
    const user = this.lundifyUsers.find((candidate) => candidate.id === cloudUserId);
    return user?.name ?? user?.email ?? `#${cloudUserId}`;
  }

  uploadWithoutRecipients() {
    if (!this.canUploadWithoutRecipients || !this.client || !this.file) {
      return;
    }

    this.inviting = true;
    this.documentService
      .shareDocumentToLundify(this.client.id, this.file, [], this.path)
      .pipe(finalize(() => (this.inviting = false)))
      .subscribe({
        next: (response) => {
          this.documentShareId = response.documentShareId;
          this.shared.emit();
        },
        error: () => {
          this.inviting = false;
        },
      });
  }

  invite() {
    if (!this.canInvite || !this.client || !this.selectedUserId) {
      return;
    }

    const recipient = { cloudUserId: this.selectedUserId, role: this.selectedRole };
    this.inviting = true;

    const onSuccess = () => {
      this.access = [...this.access, recipient];
      this.selectedUserId = null;
      this.selectedRole = 'READ';
      this.shared.emit();
    };
    const onError = () => {
      this.inviting = false;
    };

    if (this.documentShareId === null && this.file) {
      this.documentService
        .shareDocumentToLundify(this.client.id, this.file, [recipient], this.path)
        .pipe(finalize(() => (this.inviting = false)))
        .subscribe({
          next: (response) => {
            this.documentShareId = response.documentShareId;
            onSuccess();
          },
          error: onError,
        });
      return;
    }

    if (this.documentShareId !== null) {
      this.documentService
        .addSharedDocumentRecipient(this.client.id, this.documentShareId, recipient)
        .pipe(finalize(() => (this.inviting = false)))
        .subscribe({ next: onSuccess, error: onError });
    }
  }

  changeRole(granted: GrantedAccess, role: LundifyShareRecipientRole) {
    if (
      !this.client ||
      this.documentShareId === null ||
      granted.role === role ||
      this.pendingRoleChanges.has(granted.cloudUserId)
    ) {
      return;
    }

    const previousRole = granted.role;
    let updated = false;
    this.pendingRoleChanges = new Set(this.pendingRoleChanges).add(granted.cloudUserId);
    granted.role = role;
    this.documentService
      .changeSharedDocumentRecipientRole(this.client.id, this.documentShareId, granted.cloudUserId, role)
      .pipe(
        finalize(() => {
          this.pendingRoleChanges.delete(granted.cloudUserId);
          if (!updated) {
            granted.role = previousRole;
          }
        }),
      )
      .subscribe({
        next: () => {
          updated = true;
          this.shared.emit();
        },
        error: () => {
          granted.role = previousRole;
        },
      });
  }

  remove(granted: GrantedAccess) {
    if (!this.client || this.documentShareId === null) {
      return;
    }

    this.documentService.removeSharedDocumentRecipient(this.client.id, this.documentShareId, granted.cloudUserId).subscribe({
      next: () => {
        this.access = this.access.filter((candidate) => candidate !== granted);
        this.shared.emit();
      },
    });
  }

  close() {
    if (this.inviting) {
      this._visible = true;
      return;
    }

    // Closing the overlay before the file was uploaded means it was never sent to Lundify.
    if (this.file && !this.uploaded) {
      this.store.dispatch(
        ToastActions.showInfoMessage({
          summary: 'Filen delades inte',
          detail: 'Filen skickades inte till Lundify eftersom ingen mottagare bjöds in.',
        }),
      );
    }
    this._visible = false;
    this.visibleChange.emit(false);
  }

  private loadLundifyUsers() {
    if (!this.client) {
      return;
    }

    this.loadingUsers = true;
    this.cloudCompanyService.getLundifyCompanySubscriptionUsers(this.client.id).subscribe({
      next: (users) => {
        this.lundifyUsers = (users || []).filter((user) => !!user.id);
        this.loadingUsers = false;
      },
      error: () => {
        this.lundifyUsers = [];
        this.loadingUsers = false;
      },
    });
  }

  private resetState() {
    this.documentShareId = this.sharedDocument?.documentShareId ?? null;
    this.access = (this.sharedDocument?.recipients ?? []).map((recipient) => ({
      cloudUserId: recipient.cloudUserId,
      role: recipient.role,
    }));
    this.selectedUserId = null;
    this.selectedRole = 'READ';
    this.inviting = false;
  }
}
